import { cast, types } from "mobx-state-tree";
import { StorageModel } from "../storage/StorageModel";
import { api } from "../api/ApiService";
import { Breadcrumb, BreadcrumbModel } from "./BreadcrumbModel";
import { EntityName, EntityTypeFromPathModel, EntityTypeFromPath } from "./EntityTypeFromPathModel";
import { getTextKeysAndPaths } from "./PathHelpers";
import { TranslationContext } from "../translation/TranslationService";

const BreadcrumbsModel = types.model({
  breadcrumbs: types.optional(types.array(BreadcrumbModel), []),
  entityNames: types.optional(types.array(EntityTypeFromPathModel), []),
  textKeys: types.optional(types.array(types.string), []),
  paths: types.optional(types.array(types.string), []),
  textContexts: types.array(types.optional(types.frozen<TranslationContext>(), {}))
});

const BreadcrumbsStorageModel = types
.compose(BreadcrumbsModel, StorageModel)
.named("BreadcrumbsStorageModel")
.actions((self) => {
  return {
    setEntityNames(entityNames: EntityName[]) {
      self.entityNames = cast(entityNames)
    },

    getEntityNameByName(entityName: string): string | undefined {
      return self.entityNames.find((entity) => entity.entity === entityName)?.name;
    },

    setContextsForSewerObject(textKey: string, index: number, currentPath: string) {
      if (textKey === EntityTypeFromPath.SewerObject) {
        const entitiesToCheck = ['structure', 'node', 'section'];

        entitiesToCheck.forEach((item) => {
          if (currentPath.includes(item)) {
            let textKeyToUpdate = '';
            let contextToUpdate: { [key: string]: any } = {};

            switch (item) {
              case 'structure':
                textKeyToUpdate = 'ViewStructure.Breadcrumb';
                contextToUpdate = { structureName: this.getEntityNameByName(textKey) };
                break;
              case 'section':
                textKeyToUpdate = 'ViewSection.Breadcrumb';
                contextToUpdate = { sectionName: this.getEntityNameByName(textKey) };
                break;
              case 'node':
                textKeyToUpdate = 'ViewNode.Breadcrumb';
                contextToUpdate = { nodeName: this.getEntityNameByName(textKey) };
                break;
            }

            self.textKeys[index + 1] = textKeyToUpdate;
            self.textContexts[index + 1] = contextToUpdate;
          }
        });
      }
    },

    setContextForInspection(textKey: string, index: number) {
      if (textKey === EntityTypeFromPath.Inspection && Number(self.textKeys[index + 1])) {
        self.textKeys[index + 1] = 'general.inspections.name';
        self.textContexts[index + 1] = { inspectionName: this.getEntityNameByName(textKey)! };
      }
    },

    setContextForDamage(textKey: string, index: number) {
      if (textKey === EntityTypeFromPath.Damage && Number(self.textKeys[index + 1])) {
        self.textKeys[index + 1] = 'ViewDamage.Breadcrumb';
        self.textContexts[index + 1] = { damageId: this.getEntityNameByName(textKey)! };
      }
    },

    mapIdToEntityName(textKey: string, index: number) {
      if (Object.values(EntityTypeFromPath).includes(textKey as any) && Number(self.textKeys[index + 1])) {
        self.textKeys[index + 1] = self.entityNames.find((entityName) =>
          entityName.entity === textKey)?.name!;
      }
    },

    translateTextKeys(textKey: string, index: number) {
      let textKeysTranslations = new Map<string, string>([
        ['jobs', 'general.jobs'],
        ['projects', 'general.projects'],
        ['sewer-objects', 'general.sewer-objects'],
        ['create-project', 'CreateProject.add-new'],
        ['create-job', 'CreateJob.add'],
        ['members', 'general.projects.members'],
        ['map', 'general.sewer-objects.map'],
        ['edit', 'general.edit'],
        ['inspections', 'general.inspections'],
        ['report', 'general.report'],
        ['files', 'general.inspections.files'],
        ['damages', 'general.damages'],
        ['analysis', 'general.analysis'],
        ['create-damage', 'CreateDamage.add-new'],
        ['linked-objects', 'general.linked-objects']
      ]);

      if (Array.from(textKeysTranslations.keys()).includes(textKey)){
        self.textKeys[index] = textKeysTranslations.get(textKey)!;
      }
    },

    addBreadcrumb(index: number, hasPath: boolean) {
      self.breadcrumbs.push(cast({
        textKey: self.textKeys[index],
        to: hasPath ? self.paths[index] : '',
        textContext: self.textContexts[index]
      }));
    },

    trimBreadcrumbs(maxBreadcrumbsLength: number) {
      const areBreadcrumbsTooMany = self.textKeys.length > maxBreadcrumbsLength;
      const firstBreadcrumbIndex = areBreadcrumbsTooMany ? self.textKeys.length - maxBreadcrumbsLength : 0;

      if (areBreadcrumbsTooMany){
        self.breadcrumbs = cast(self.breadcrumbs.slice(firstBreadcrumbIndex));
        self.breadcrumbs.unshift({ textKey: '...', isEllipsis: true } as Breadcrumb);
      }
    },

    updateBreadcrumbs(currentPath: string) {
      this.clearBreadcrumbs();

      const [ textKeys, paths ] = getTextKeysAndPaths(currentPath);
      self.textKeys = cast(textKeys);
      self.paths = cast(paths);

      self.textKeys.forEach((textKey, index) => {
        this.mapIdToEntityName(textKey, index);
        this.setContextsForSewerObject(textKey , index, currentPath);
        this.setContextForInspection(textKey, index);
        this.setContextForDamage(textKey, index);
        this.translateTextKeys(textKey, index);
        this.addBreadcrumb(index, self.textKeys.length-1 !== index);
      })

      this.trimBreadcrumbs(5);
    },

    clearBreadcrumbs() {
      self.breadcrumbs.clear();
      self.textKeys.clear();
    }
  }
});

export const breadcrumbsStorage = BreadcrumbsStorageModel.create({ autoLoad: false }, { api });
